import { useEffect, useState, ReactNode, FC } from 'react';
import { IntlProvider as BaseIntlProvider } from 'react-intl';
import { useRecoilValue } from 'recoil';

import en from './lang/en.json';
import es from './lang/es.json';
import { localeState } from './state/atoms/localeAtoms';

/** Message id known to the canonical (en) catalog; a typo fails to compile. */
export type CatalogMessageId = keyof typeof en;

export type Messages = {
    [key: string]: string;
};

const messages: Record<string, Messages> = {
    en,
    es,
};

/** The locale every other locale falls back to; always fully populated. */
const DEFAULT_LOCALE = 'en';

/**
 * A locale's messages laid over the default locale's. Translations here are
 * partial by policy — `es` trails `en` by a couple of hundred keys — and
 * react-intl renders a missing key as its raw message id. That is merely ugly
 * for a label, but it turns the bulk-DELETE confirmation into an unreadable
 * dialog asking the user to approve an irreversible action described only as
 * `structure.bulk.deletionConfirmation`. Falling back to English keeps every
 * such prompt legible, and keeps the next partially-translated feature from
 * reintroducing the same hazard.
 *
 * This does NOT excuse leaving keys untranslated — it bounds the damage when a
 * key is missed. The default locale's own object is returned as-is so the
 * common case allocates nothing.
 */
function messagesWithFallback(locale: string): Messages {
    if (locale === DEFAULT_LOCALE) {
        return messages[DEFAULT_LOCALE];
    }
    return { ...messages[DEFAULT_LOCALE], ...messages[locale] };
}

/**
 * A simple localization class for non-React code that piggybacks on the same messages
 * used by react-intl in the React part of the application.
 *
 * A more robust solution would involve using `createIntl` from `react-intl`, but that would
 * require refactoring how i18n is initialized in the app and how it integrates with Recoil state.
 */
class Localization {
    private language: string;
    private defaultLang: string;
    private translations: Messages;

    constructor(defaultLang: string) {
        this.defaultLang = defaultLang;
        this.language = defaultLang;
        this.translations = messages[defaultLang] || {};
    }

    /**
     * Sets the current language for translations.
     * If the specified language exists in the messages object, updates the language and translations accordingly.
     *
     * NOTE: calling this does NOT update any React components using the IntlProvider. Preferred way is to change
     * the locale using IntlProvider and Recoil state and not call this directly.
     *
     * @param lang - The language code to set (e.g., 'en', 'fr').
     */
    setLanguage(lang: string) {
        if (messages[lang]) {
            this.language = lang;
            this.translations = messages[lang];
        }
    }

    /**
     * Retrieves the localized string for the given key, with optional interpolation.
     *
     * The method first attempts to find the translation in the current language's translations.
     * If not found, it falls back to the default language's messages.
     * If the key is still not found, it returns the key itself.
     *
     * Supports simple {param} placeholders in message strings.
     *
     * @usage
     * localization.t('greeting', { name: 'Alice' });
     * // If the message for 'greeting' is "Hello, {name}!", this returns "Hello, Alice!"
     */
    t(key: string, params?: Record<string, string | number>): string {
        let msg =
            this.translations[key] || messages[this.defaultLang][key] || key;
        if (params) {
            msg = msg.replace(/{(\w+)}/g, (_, key) =>
                String(params[key] ?? `{${key}}`)
            );
        }
        return msg;
    }

    getLanguage(): string {
        return this.language;
    }
}

// Singleton instance for use outside React
export const localization = new Localization('en');

// React provider, syncs with Recoil
export const IntlProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const stateLocale = useRecoilValue(localeState);
    const locale = Object.keys(messages).includes(stateLocale)
        ? stateLocale
        : DEFAULT_LOCALE;
    const [intlMessages, setIntlMessages] = useState(() =>
        messagesWithFallback(locale)
    );

    useEffect(() => {
        setIntlMessages(messagesWithFallback(locale));
        localization.setLanguage(locale);
    }, [locale]);

    return (
        <BaseIntlProvider
            locale={locale}
            defaultLocale={DEFAULT_LOCALE}
            messages={intlMessages}
        >
            {children}
        </BaseIntlProvider>
    );
};

/**
 * Exports the `Localization` class for use in tests only.
 * This export is intended for testing purposes and should not be used in production code.
 *
 * @internal
 */
export const Localization__TESTING_ONLY_ = Localization;

/**
 * Exports the `messages` object for use in tests only.
 * This export is intended for testing purposes and should not be used in production code.
 *
 * @internal
 */
export const messages__TESTING_ONLY_ = messages;

/**
 * Exports the locale-fallback merge for use in tests only, so the guard against
 * a raw message id reaching a clinical prompt is asserted against the function
 * the provider actually uses rather than a re-implementation of it.
 *
 * @internal
 */
export const messagesWithFallback__TESTING_ONLY_ = messagesWithFallback;
